// src/shell/shell.js — рамката: меню, горна лента, избор на фирма, потребителско
// меню, диспечер на изгледите. Пренесено дословно от app.js (Фаза 3.3).

import { el, clear, $, initials } from '../shared/dom.js';
import { api } from '../shared/api.js';
import { companyLogo } from '../shared/companyLogo.js';
import { state, activeCompanyObj, isReadOnlyView } from '../shared/state.js';
import { themeSwitch, syncAppearance } from './theme.js';
import { renderAuth } from './auth.js';
import { MODULES, SETTINGS_GROUP } from './modules.js';
import { ICONS } from './icons.js';
import { openCompanySettings } from '../companies/index.js';
import { changePasswordDialog } from './password.js';
import { watchLists, watchTopbar } from '../shared/stickyList.js';

// Менюто идва от ЕДИННИЯ регистър (src/shell/modules.js) — същият ред и
// групи като плочките на началния екран. Никакъв втори списък.
const NAV = MODULES.map((m) => ({ id: m.id, label: m.label, ic: m.icon, group: m.group, adminOnly: !!m.adminOnly, ownerOnly: !!m.ownerOnly, mobileOnly: !!m.mobileOnly, helpAnchor: m.helpAnchor || '' }));

// Долното меню на телефон (под 768 px, ПРОМТ-37): четирите раздела, които се
// ползват от телефона, + „Още…", което отваря цялото меню. Нищо не е махнато
// и правата са същите — бутон за раздел без право просто не се рисува.
// „Доставки" на телефон води към „Качи от телефона": там редове не се пипат.
export const MOBILE_NAV = ['invoices', 'proformas', 'purchases', 'contacts'];
let _refocusMore = false;

// Разделите само за собственика (поверителните пари на кантората). Списъкът се
// чете от регистъра, за да няма втори списък, който да се разсинхронизира.
// ВАЖНО: това е само козметика — истинската защита е ownerRequired на сървъра.
const OWNER_ONLY = new Set(MODULES.filter((m) => m.ownerOnly).map((m) => m.id));

// „Настройки" в менюто е свита по подразбиране (стъпка 4): там е рядко
// ползваното и без нея менюто не се побираше на лаптоп. Изборът се помни.
// Екран от групата, отворен по друг път (адрес, бутон „? Помощ", подкана
// „изберете фирма"), разгъва групата веднъж — иначе текущият раздел би бил
// скрит; после човек може пак да я свие, без тя да се отваря на всяко
// пречертаване.
const NAV_SETTINGS_KEY = 'navSettingsOpen';
let _navSeenView = null;
function navSettingsOpen() { try { return localStorage.getItem(NAV_SETTINGS_KEY) === '1'; } catch (e) { return false; } }
function setNavSettingsOpen(on) { try { localStorage.setItem(NAV_SETTINGS_KEY, on ? '1' : '0'); } catch (e) {} }

// Горе вляво стои фирмата, в която се работи — логото и името ѝ, а отдолу
// с дребни букви „БВ КОНТ" (Макс, 19.10.2026).
function brandBlock() {
  const c = activeCompanyObj();
  if (!c) {
    return el('div', { class: 'brand' }, el('img', { class: 'mark', src: '/logo.png', alt: '' }),
      el('div', { class: 'brand-text' }, el('span', { class: 'brand-name', text: 'БВ КОНТ' }), el('span', { class: 'brand-sub', text: 'CRM система' })));
  }
  // Докато логото не е дошло (или фирмата няма лого), стоят инициалите в цвета ѝ.
  const mark = el('span', { class: 'mark brand-initials', 'aria-hidden': 'true', text: initials(c.name || '') });
  const box = el('div', { class: 'brand' }, mark,
    el('div', { class: 'brand-text' }, el('span', { class: 'brand-name', text: c.name || '', title: c.name || '' }), el('span', { class: 'brand-sub', text: 'БВ КОНТ' })));
  // alt е празен: името на фирмата е веднага до логото — иначе четецът го казва два пъти.
  companyLogo(c).then((src) => { if (src && mark.parentNode) mark.replaceWith(el('img', { class: 'mark brand-logo', src, alt: '' })); });
  return box;
}

function setCompanyColor() {
  const c = activeCompanyObj();
  document.documentElement.style.setProperty('--company', (c && c.color) || '#0f766e');
}

// ---- Разделът живее в адреса (Фаза А.7) ----
// '#/invoices' → раздел (ключовете от регистъра — НЕ втори списък имена);
// '#h-…' → котва в Помощта (тя е единствената с котви). Така F5 връща същия
// екран, а „Назад" върви по историята на разделите, вместо да изхвърля от
// системата. Екраните НЕ знаят за адреси: огледалото е само в renderShell.
function viewFromHash() {
  const h = String(location.hash || '');
  if (h.startsWith('#/')) { const id = h.slice(2); return MODULES.some((m) => m.id === id) ? id : null; }
  if (h.startsWith('#h-')) return 'help';
  return null;
}

export function initRouting() {
  const id = viewFromHash();
  if (id) state().view = id;
  window.addEventListener('hashchange', () => {
    const next = viewFromHash();
    // Непознат хеш — не пипаме; същият раздел (котва в Помощта) — браузърът
    // сам скролира, прерисуване би съборило мястото.
    if (!next || next === state().view) return;
    state().view = next;
    state().sidebarOpen = false;
    renderShell();
  });
}

// ---- Състояния на менюто (широк екран) ----
// (Г.2) ДВЕ състояния: пълно ⇄ скрито. Тристепенното (с междинното „само
// икони") беше класическото „изгубих си менюто": новак натиска веднъж,
// надписите изчезват, натиска пак — менюто го няма. Иконите са абстрактни
// (▦ ◈ ▤) и режимът вредеше повече, отколкото пестеше място. Под 860 px
// менюто си остава изплъзващо със затъмнение.
const SIDEBAR_MODES = ['full', 'hidden'];
// Надписът на бутона казва какво ще СТАНЕ при натискане.
const NEXT_ACTION = { full: 'Скрий менюто', hidden: 'Покажи менюто' };
const MODE_SAID = { full: 'Менюто е показано', hidden: 'Менюто е скрито' };

// Завареното „icons" (и всичко непознато) се чете като „пълно" — никой не
// остава залепен в мъртвия режим от старата версия.
function normalizeMode(m) { return SIDEBAR_MODES.includes(m) ? m : 'full'; }

function isNarrow() {
  return !!(window.matchMedia && window.matchMedia('(max-width: 860px)').matches);
}

function setSidebarMode(m) {
  state().sidebarMode = m;
  try { localStorage.setItem('sidebarMode', m); } catch (e) {}
}

// Смяната е чисто визуална — за екранния четец я изричаме през живата област
// #toasts. Тя живее ИЗВЪН #app и затова преживява прерисуването на рамката.
function announce(msg) {
  const host = document.getElementById('toasts');
  if (!host) return;
  const s = el('span', { class: 'sr-only', text: msg });
  host.appendChild(s);
  setTimeout(() => { if (s.parentNode) s.remove(); }, 4000);
}

// Прерисуването пресъздава ☰ и фокусът пада на <body>. При бутон, който се
// върти през състояния, това значи, че вторият натиск отива в празното.
let _refocusHamburger = false;
// Същото за „Настройки" в менюто: свиване и разгъване с клавиатура.
let _refocusNavToggle = false;

export function renderShell() {
  syncAppearance();
  const st = state();
  setCompanyColor();
  // Адресът огледално следва раздела — така всяко място, което сменя view и
  // вика renderShell, получава F5/„Назад" наготово. pushState НЕ пуска
  // hashchange, затова няма двойно прерисуване; еднакъв хеш не трупа история
  // (превключването на менюто минава оттук без нов запис).
  // Котва на Помощта (#h-…) е легитимен адрес на екрана 'help' — не се
  // пренаписва, иначе контекстният „?" губи мястото си (Г.9).
  try {
    const isHelpAnchor = st.view === 'help' && String(location.hash).startsWith('#h-');
    if (!isHelpAnchor && location.hash !== '#/' + st.view) history.pushState(null, '', '#/' + st.view);
  } catch (e) {}
  const root = $('#app');
  clear(root);

  const nav = el('nav', { class: 'nav', 'aria-label': 'Основна навигация' });
  const isAdmin = !!(st.user && st.user.role === 'admin');
  // ownerOnly се проверява и тук, и във viewAllowed — първото маха бутона,
  // второто пази диспечера (адрес, набран на ръка, не бива да отваря екрана).
  const isOwner = !!(st.user && st.user.owner === true);
  // Раздел, изключен само заради настройката на фирмата (стъпка 6), остава в
  // менюто — бледо, с „изкл." — вместо да изчезне без следа.
  const navItems = NAV.filter((n) => (viewAllowed(n.id) || moduleOff(n.id)) && (!n.adminOnly || isAdmin) && (!n.ownerOnly || isOwner));
  const inSettings = navItems.some((n) => n.group === SETTINGS_GROUP && n.id === st.view);
  if (inSettings && _navSeenView !== st.view) setNavSettingsOpen(true);
  _navSeenView = st.view;
  const settingsOpen = navSettingsOpen();
  let lastGroup = null;
  let host = nav;
  navItems.forEach((n) => {
    if (n.group !== lastGroup) {
      lastGroup = n.group;
      if (n.group === SETTINGS_GROUP) {
        // Бутон-заглавие: името му е видимият текст, стрелката е в CSS.
        // Точките остават в DOM (hidden) — менюто е пълно копие на регистъра.
        host = el('div', { id: 'nav-settings', class: 'nav-group', hidden: settingsOpen ? null : '' });
        nav.appendChild(el('button', {
          class: 'nav-cap nav-toggle', type: 'button', text: SETTINGS_GROUP,
          'aria-expanded': settingsOpen ? 'true' : 'false', 'aria-controls': 'nav-settings',
          onClick: () => { setNavSettingsOpen(!settingsOpen); _refocusNavToggle = true; renderShell(); },
        }));
        nav.appendChild(host);
      } else {
        host = nav;
        nav.appendChild(el('div', { class: 'nav-cap', text: n.group }));
      }
    }
    const off = !viewAllowed(n.id);
    host.appendChild(el('button', {
      class: (st.view === n.id ? 'active' : '') + (off ? ' nav-off' : '') + (n.mobileOnly ? ' nav-mobile-only' : ''),
      type: 'button',
      // Кой раздел е текущият — досега личеше само по цвят.
      'aria-current': st.view === n.id ? 'page' : null,
      // В режим „само икони" надписът се скрива ВИЗУАЛНО (display:none), а с
      // него би изчезнало и достъпното име. Затова то стои в aria-label —
      // същият текст, така че видимото и изговаряното не се разминават.
      'aria-label': off ? n.label + ' — разделът не е включен за тази фирма' : n.label,
      onClick: () => { st.view = n.id; st.sidebarOpen = false; renderShell(); },
      // Иконата е украса: без aria-hidden четецът изговаря името на знака
      // („черен квадрат") преди всеки надпис.
    }, el('span', { class: 'ic', 'aria-hidden': 'true', html: ICONS[n.ic] || '' }), el('span', { text: n.label }),
    off ? el('span', { class: 'nav-off-tag', 'aria-hidden': 'true', text: 'изкл.' }) : null));
  });

  const narrow = isNarrow();
  // На тесен екран рейлът не важи: там менюто е изплъзващо и се води от
  // sidebarOpen, затова подаваме 'full' и оставяме медия заявката да реши.
  const mode = narrow ? 'full' : normalizeMode(st.sidebarMode || 'full');
  const railHidden = mode === 'hidden';
  const sidebar = el('aside', {
    id: 'side-menu',
    class: 'sidebar' + (st.sidebarOpen ? ' open' : ''),
    // Скритото меню не бива да се хваща с Tab — иначе фокусът изчезва зад
    // ръба на екрана и не личи къде е.
    inert: railHidden ? 'true' : null,
    'aria-hidden': railHidden ? 'true' : null,
  },
    brandBlock(),
    nav
  );

  const title = (navItems.find((n) => n.id === st.view) || {}).label || '';
  const topbar = el('header', { class: 'topbar' },
    el('button', {
      class: 'hamburger', type: 'button',
      'aria-label': narrow
        ? (st.sidebarOpen ? 'Скрий менюто' : 'Покажи менюто')
        : NEXT_ACTION[mode],
      onClick: () => {
        if (isNarrow()) {
          st.sidebarOpen = !st.sidebarOpen;
          announce(st.sidebarOpen ? 'Менюто е отворено' : 'Менюто е затворено');
        } else {
          const next = SIDEBAR_MODES[(SIDEBAR_MODES.indexOf(normalizeMode(st.sidebarMode || 'full')) + 1) % SIDEBAR_MODES.length];
          setSidebarMode(next);
          announce(MODE_SAID[next]);
        }
        _refocusHamburger = true;
        renderShell();
      },
    }, el('span', { 'aria-hidden': 'true', text: '☰' })),
    el('h1', { text: title }),
    companySelector(),
    // „Как да…?" — до Помощта, защото там го търсят хората, които се лутат.
    el('button', { class: 'btn sm howto-btn', type: 'button', text: 'Как да…?', title: 'Стъпка по стъпка: как се прави', onClick: () => { st.view = 'howto'; st.sidebarOpen = false; renderShell(); } }),
    // (Г.9) Контекстният „?": отваря Помощта на секцията на ТЕКУЩИЯ екран.
    el('button', { class: 'btn sm ghost help-btn', text: '? Помощ', title: 'Отвори помощта за този екран', style: 'margin-left:8px', onClick: () => {
      // Екран с няколко раздела може да посочи по-точна котва (складът:
      // „Продажби от POS"/„Рецептури" → h-pos); иначе котвата от регистъра.
      const mod = window.CRM_MODULES && window.CRM_MODULES[st.view];
      let dyn = '';
      try { dyn = mod && typeof mod.helpAnchor === 'function' ? mod.helpAnchor() : ''; } catch (e) { dyn = ''; }
      const anchor = dyn || (NAV.find((n) => n.id === st.view) || {}).helpAnchor;
      st.view = 'help'; st.sidebarOpen = false;
      // pushState, не replaceState: „Назад“ от Помощта връща на екрана, от
      // който е отворена, а не го прескача (рецензия 27.10.2026, т. 4.1).
      if (anchor) { try { history.pushState(null, '', '#' + anchor); } catch (e) {} }
      renderShell();
    } }),
    userMenu()
  );

  // tabindex="-1": за да може връзката „Към съдържанието" наистина да
  // премести фокуса тук, а не само да превърти страницата.
  // Гъстите таблици дишат на пълна ширина; за текст 1180 px си остава
  // правилният таван (редът не бива да стане километричен).
  // „Такси" е матрица клиент × 12 месеца — най-широкият екран в системата.
  const WIDE_VIEWS = ['invoices', 'proformas', 'contacts', 'vehicles', 'protocols', 'blanka', 'checklist', 'audit', 'fees', 'purchases'];
  const page = el('main', { class: 'page' + (WIDE_VIEWS.includes(st.view) ? ' page-wide' : ''), id: 'page', tabindex: '-1' });
  const content = el('div', { class: 'content' }, topbar, page);
  const scrim = el('div', { class: 'scrim' + (st.sidebarOpen ? ' show' : ''), onClick: () => { st.sidebarOpen = false; renderShell(); } });

  // Първото фокусируемо нещо на страницата: без него всеки, който работи с
  // клавиатура, минава през 16-те бутона на менюто на ВСЯКА страница.
  // Без смяна на адреса: href="#page" правеше хеша „#page“ и F5 връщаше на
  // таблото (рецензия 27.10.2026, т. 4.1). Фокусът отива на съдържанието.
  root.appendChild(el('a', { class: 'skip-link', href: '#page', text: 'Към съдържанието', onClick: (e) => {
    e.preventDefault();
    const pg = document.getElementById('page');
    if (pg) { pg.focus(); if (pg.scrollIntoView) pg.scrollIntoView(); }
  } }));
  root.appendChild(el('div', { class: 'company-bar' }));
  // Ширината на менюто се сменя през --sidebar-w на .app: така .content се
  // мести сам, без второ правило за всяко състояние.
  root.appendChild(el('div', { class: 'app', 'data-sidebar': mode }, sidebar, content));
  root.appendChild(scrim);
  root.appendChild(mobileNav(navItems));

  // Фиксираните филтри и заглавен ред (ПРОМТ-37): наблюдателят е преди
  // екрана, защото екраните чертаят таблиците си след зареждането.
  watchTopbar(topbar);
  watchLists(page);
  renderView(page);

  if (_refocusMore) {
    _refocusMore = false;
    const m = root.querySelector('.mobile-nav-more');
    if (m) m.focus();
  }
  if (_refocusHamburger) {
    _refocusHamburger = false;
    const h = root.querySelector('.hamburger');
    if (h) h.focus();
  }
  if (_refocusNavToggle) {
    _refocusNavToggle = false;
    const t = root.querySelector('.nav-toggle');
    if (t) t.focus();
  }
}

// Долното меню на телефон. navItems са вече филтрирани по права (същите като
// в страничното меню) — тук само се избират четирите от MOBILE_NAV.
function mobileNav(navItems) {
  const st = state();
  const bar = el('nav', { class: 'mobile-nav', 'aria-label': 'Бързо меню' });
  MOBILE_NAV.forEach((id) => {
    const n = navItems.find((x) => x.id === id);
    if (!n) return;
    // „Доставки" отваря „Качи от телефона", ако фирмата ги ползва; иначе
    // обичайния екран, който обяснява как се включват.
    const target = id === 'purchases' && viewAllowed('phoneupload') ? 'phoneupload' : id;
    const on = st.view === id || st.view === target;
    const off = !viewAllowed(id);
    bar.appendChild(el('button', {
      type: 'button', class: 'mobile-nav-btn' + (on ? ' active' : '') + (off ? ' nav-off' : ''),
      'aria-current': on ? 'page' : null,
      'aria-label': off ? n.label + ' — разделът не е включен за тази фирма' : n.label,
      onClick: () => { st.view = target; st.sidebarOpen = false; renderShell(); },
    }, el('span', { class: 'ic', 'aria-hidden': 'true', html: ICONS[n.ic] || '' }), el('span', { text: n.label })));
  });
  bar.appendChild(el('button', {
    type: 'button', class: 'mobile-nav-btn mobile-nav-more',
    'aria-label': 'Още раздели — цялото меню', 'aria-controls': 'side-menu',
    'aria-expanded': st.sidebarOpen ? 'true' : 'false',
    onClick: () => { st.sidebarOpen = !st.sidebarOpen; _refocusMore = true; announce(st.sidebarOpen ? 'Менюто е отворено' : 'Менюто е затворено'); renderShell(); },
  }, el('span', { class: 'ic', 'aria-hidden': 'true', html: ICONS.more }), el('span', { text: 'Още…' })));
  return bar;
}

// Над колко фирми се показва търсачката в менюто за избор.
const COMPANY_SEARCH_MIN = 7;

function companySelector() {
  const st = state();
  const active = activeCompanyObj();
  const wrap = el('div', { class: 'company-select' });
  const pill = el('button', {
    class: 'company-pill', type: 'button',
    'aria-haspopup': 'true', 'aria-expanded': 'false',
    'aria-label': 'Активна фирма: ' + (active ? active.name : 'няма избрана') + '. Смяна на фирма',
  },
    el('span', { class: 'dot', 'aria-hidden': 'true' }),
    el('span', { class: 'company-identity' }, el('span', { class: 'company-caption', text: 'Работите във фирма' }), el('span', { text: active ? active.name : 'Изберете фирма' })),
    el('span', { class: 'chev', 'aria-hidden': 'true', text: '▾' })
  );
  let menu = null;
  pill.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menu) { menu.remove(); menu = null; pill.setAttribute('aria-expanded', 'false'); return; }
    menu = el('div', { class: 'menu menu-companies' });
    const closeMenu = () => { if (menu) { menu.remove(); menu = null; pill.setAttribute('aria-expanded', 'false'); } };

    if (st.companies.length === 0) {
      menu.appendChild(el('button', { text: 'Добавете първата си фирма', onClick: () => { closeMenu(); st.view = 'companies'; renderShell(); } }));
    }

    const list = el('div', { class: 'menu-list' });
    const drawList = (q) => {
      clear(list);
      const hits = st.companies.filter((c) => !q || String(c.name || '').toLowerCase().includes(q));
      if (st.companies.length && !hits.length) {
        list.appendChild(el('div', { class: 'menu-empty', text: 'Няма фирма с такова име.' }));
      }
      hits.forEach((c) => {
        list.appendChild(el('button', {
          onClick: () => { st.activeCompany = c.id; localStorage.setItem('activeCompany', c.id); closeMenu(); renderShell(); },
        }, el('span', { class: 'dot', style: 'background:' + (c.color || '#0f766e') }), el('span', { text: c.name }),
          st.activeCompany === c.id ? el('span', { text: '✓', style: 'margin-left:auto;color:var(--accent)' }) : null));
      });
    };

    // Бърза търсачка при по-дълъг списък: филтрира на живо, Enter избира
    // първото съвпадение.
    if (st.companies.length >= COMPANY_SEARCH_MIN) {
      const search = el('input', { type: 'search', class: 'menu-search', placeholder: 'Търсене на фирма…', 'aria-label': 'Търсене на фирма' });
      search.addEventListener('input', () => drawList(search.value.trim().toLowerCase()));
      search.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { const first = list.querySelector('button'); if (first) first.click(); }
      });
      menu.appendChild(search);
      // Автофокус само с мишка/тракпад: на телефон би отворил клавиатурата
      // върху самия списък.
      if (window.matchMedia && matchMedia('(hover:hover) and (pointer:fine)').matches) {
        setTimeout(() => search.focus(), 0);
      }
    }

    drawList('');
    menu.appendChild(list);
    // Escape затваря менюто (ако търсачката има текст, първият Escape само
    // го чисти — родно поведение на search полето).
    menu.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (e.target.classList && e.target.classList.contains('menu-search') && e.target.value) return;
      closeMenu(); pill.focus();
    });
    wrap.appendChild(menu);
    pill.setAttribute('aria-expanded', 'true');
    // Затваряне само при клик ИЗВЪН менюто — иначе scroll лентата и
    // търсачката биха го затваряли (кликът върху фирма затваря сам).
    setTimeout(() => document.addEventListener('click', function close(ev) {
      if (menu && wrap.contains(ev.target)) return;
      closeMenu();
      document.removeEventListener('click', close);
    }), 0);
  });
  wrap.appendChild(pill);
  return wrap;
}

function userMenu() {
  const st = state();
  const wrap = el('div', { class: 'company-select' });
  // Инициалите са картинка от букви: истинското име отива в aria-label.
  const btn = el('button', {
    class: 'avatar', type: 'button',
    'aria-haspopup': 'true', 'aria-expanded': 'false',
    'aria-label': 'Профил и настройки: ' + st.user.name,
    text: initials(st.user.name),
  });
  let menu = null;
  const shut = () => { if (menu) { menu.remove(); menu = null; } btn.setAttribute('aria-expanded', 'false'); };
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menu) { shut(); return; }
    menu = el('div', { class: 'menu' },
      el('div', { style: 'padding:8px 11px' }, el('div', { style: 'font-weight:700', text: st.user.name }), el('div', { style: 'font-size:13px;color:var(--muted)', text: st.user.email })),
      el('hr'),
      el('div', { style: 'padding:6px 11px' }, el('div', { style: 'font-size:12px;color:var(--muted);margin-bottom:5px', text: 'Тема' }), themeSwitch()),
      el('hr'),
      // Своята парола — без да се чака администраторът (грешка от картата, 16.10.2026).
      el('button', { type: 'button', text: 'Смени паролата', onClick: () => { shut(); changePasswordDialog(() => { st.user = null; renderAuth(); }); } }),
      el('button', { type: 'button', text: 'Изход', onClick: async () => { shut(); await api('/auth/logout', { method: 'POST' }); st.user = null; renderAuth(); } })
    );
    menu.addEventListener('keydown', (e) => { if (e.key === 'Escape') { shut(); btn.focus(); } });
    wrap.appendChild(menu);
    btn.setAttribute('aria-expanded', 'true');
    // Клик вътре в менюто не го затваря (темите сами прерисовват рамката).
    setTimeout(() => document.addEventListener('click', function close(ev) {
      if (menu && wrap.contains(ev.target)) return;
      shut();
      document.removeEventListener('click', close);
    }), 0);
  });
  wrap.appendChild(btn);
  return wrap;
}

// Изисква избрана фирма за екраните, които работят по фирма.
export function needsCompany(page, msg) {
  page.appendChild(el('div', { class: 'panel' }, el('div', { class: 'empty' },
    el('div', { class: 'big', text: '⌂' }),
    el('p', { text: msg || 'Първо изберете или създайте фирма, за да продължите.' }),
    el('button', { class: 'btn', text: 'Към моите фирми', onClick: () => { state().view = 'companies'; renderShell(); } })
  )));
}

// Дали текущият потребител има достъп до даден раздел.
// ПРОМТ-7: екран, описан в регистъра с companyModule, е позволен само когато
// АКТИВНАТА ФИРМА има модула включен (company.modules[...] === true) —
// правата на човека са нужни, но не стигат. Смяната на фирмата преначертава
// менюто (renderShell), затова проверката е жива.
// Разделът е скрит САМО заради настройката на активната фирма (склад,
// доставки): човекът има право, но фирмата не ползва модула. Тогава менюто
// го показва бледо и екранът обяснява как се включва. Без право — нищо
// (не подсказваме разделите, до които човек няма достъп).
export function moduleOff(id) {
  const mod = MODULES.find((m) => m.id === id);
  if (!mod || !mod.companyModule || !activeCompanyObj()) return false;
  return permAllowed(id) && !viewAllowed(id);
}

function renderModuleOff(page, id) {
  const st = state();
  const mod = MODULES.find((m) => m.id === id);
  const c = activeCompanyObj();
  // „Склад и Конто" се пипа от кантората — админ и потребител (Промени 16.10,
  // т. 1); клиентът моли кантората.
  const isStaff = !!(st.user && st.user.role !== 'client');
  page.appendChild(el('div', { class: 'panel module-off' },
    el('div', { class: 'panel-head' }, el('h2', { text: 'Разделът „' + mod.label + '“ не е включен за „' + c.name + '“' })),
    el('div', { class: 'module-off-body' },
      el('p', {}, el('b', { text: 'Какво съдържа: ' }), mod.desc + '.'),
      el('p', { text: mod.offHint }),
      isStaff ? null : el('p', { text: 'Включването е за счетоводната кантора — помолете я да го включи за тази фирма.' }),
      el('div', { class: 'module-off-actions' },
        isStaff ? el('button', { class: 'btn', type: 'button', text: 'Отвори настройките на фирмата', onClick: () => openCompanySettings(c.id, 'warehouse') }) : null,
        el('button', { class: 'btn ghost', type: 'button', text: 'Към таблото', onClick: () => { st.view = 'dashboard'; renderShell(); } })))));
}

function markReadOnly(page) {
  page.classList.add('readonly-view');
  if (page.querySelector(':scope > .readonly-banner')) return;
  page.insertBefore(el('div', { class: 'readonly-banner', role: 'status', text: '👁 Само преглед — можете да разглеждате, но не и да променяте в този раздел. За промени — към администратора.' }), page.firstChild);
}

export function viewAllowed(id) {
  if (!permAllowed(id)) return false;
  const mod = MODULES.find((m) => m.id === id);
  if (mod && mod.companyModule) {
    const c = activeCompanyObj();
    // Складът (ПРОМТ-8) следва ФУНКЦИЯТА на склада (warehouse_mode), не
    // отметката „Склад": сървърът изписва от фактурите и пуска /api/stock
    // точно по warehouse_mode — фирма „Търговия" без отметка иначе би
    // изписвала стока, без да вижда склада си.
    if (mod.companyModule === 'warehouse') return !!(c && (c.warehouse_mode === 'trade' || c.warehouse_mode === 'materials'));
    return !!(c && c.modules && c.modules[mod.companyModule] === true);
  }
  return true;
}

function permAllowed(id) {
  const st = state();
  // Поверителните раздели са само за собственика. Проверката е ПЪРВА, преди
  // всичко останало: администраторът минава навсякъде другаде, но собственик
  // не става от роля — флагът идва от сървъра (OWNER_EMAIL).
  if (OWNER_ONLY.has(id)) return !!(st.user && st.user.owner === true);
  // Нужни за всички. Календарът е за всички, но всеки източник в него се
  // филтрира по правата (виж services/calendar.js) — не заобикаля нищо.
  // „Как да…?" е за всички — задачите в него се филтрират поотделно
  // със същите правила (src/howto/tasks.js → visibleTasks).
  if (id === 'dashboard' || id === 'companies' || id === 'calculators' || id === 'help' || id === 'howto' || id === 'calendar') return true;
  if (!st.user) return false;
  if (st.user.role === 'admin') return true;
  if (id === 'users') return false; // само администратор
  // Заплатите са само за кантората: клиентска роля не ги вижда дори с
  // дадено право (сървърът отказва същото в routes/zaplati.js).
  if (id === 'zaplati' && st.user.role === 'client') return false;
  // „Само преглед" (id + '.read') също показва раздела — промените ги спира
  // сървърът, а екранът крие бутоните (isReadOnlyView).
  const perms = st.user.permissions || [];
  const has = (m) => perms.includes(m) || perms.includes(m + '.read');
  if (id === 'emaillog' || id === 'proformas' || id === 'articles') return has('invoices');
  // „Файлове" са оригиналите на доставките — сървърът дава само видовете, до
  // които човекът има право (routes/files.js).
  if (id === 'files') return has('purchases');
  if (id === 'phoneupload') return has('purchases');
  return has(id);
}

// Диспечер: всички екрани са модули в window.CRM_MODULES (Фаза 3.2).
export function renderView(page) {
  clear(page);
  // Нов екран започва отгоре: без това от дъното на фактурите човек се
  // озоваваше в средата на контактите. (try: jsdom няма scrollTo.)
  try { window.scrollTo(0, 0); } catch (e) {}
  const st = state();
  // Отказан раздел пада към таблото и ПОПРАВЯ адреса тихо (replaceState:
  // отказът не заслужава запис в историята).
  // Раздел, изключен само за ТАЗИ фирма, обяснява защо, вместо тихо да
  // пада към таблото (стъпка 6) — иначе изглежда като изтрит.
  if (!viewAllowed(st.view) && moduleOff(st.view)) return renderModuleOff(page, st.view);
  if (!viewAllowed(st.view)) {
    st.view = 'dashboard';
    try { history.replaceState(null, '', '#/dashboard'); } catch (e) {}
  }
  const mod = window.CRM_MODULES && window.CRM_MODULES[st.view];
  if (mod && typeof mod.view === 'function') {
    const out = mod.view(page);
    // „Само преглед": лента най-горе и клас, който крие бутоните за промяна
    // (CSS .readonly-view). Модулът чисти страницата сам — затова след него.
    if (isReadOnlyView(st.view)) Promise.resolve(out).then(() => markReadOnly(page)).catch(() => {});
    return out;
  }
  const dash = window.CRM_MODULES && window.CRM_MODULES.dashboard;
  if (dash) return dash.view(page);
}
